import {
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { LookupValue } from '../../../../clm/types/lookup-value.enum';
import { BasicPersonDetailDTO } from '../../../../ent/models/person/BasicPersonDetailDTO';
import { DateUtil } from '../../../util/DateUtil';
import { TableControl } from '../../../models/TableControl';
import { SharedService } from '../../../services/shared-service';
import { ActivityService } from '../../../../clm/services/activity.service';
import { RequestBuilderFunctions } from '../../../functions/request-builder.functions';
import { detailsExpand } from '../../../animations';
import { UtilisationPhaResponseDTO } from 'src/app/modules/clm/models/utilisation/UtilisationPhaResponseDTO';
import { UtilisationMedschemeResponseDTO } from 'src/app/modules/clm/models/utilisation/UtilisationMedschemeResponseDTO';
import { Admin } from '../../../types/portal.enum';
import { ActivityDisplayDTO } from 'src/app/modules/clm/models/utilisation/pha/ActivityDisplayDTO';
import { ActivityMedschemeDisplayDTO } from 'src/app/modules/clm/models/utilisation/medscheme/ActivityMedschemeDisplayDTO';
import { PagedFilterDTO } from '../../../models/PagedFilterDTO';
import { FormControl, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import * as moment from 'moment';

const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'YYYY',
  },
};
enum TableColumns {
  ActivityId = 'activityId',
  ActivityStartDate = 'activityStartDate',
  Type = 'type',
  Provider = 'provider',
  TariffCode = 'tariffCode',
  BenefitFamily = 'benefitFamily',
  Benefit = 'benefit', // THIS IS DUMMY COLUMN THAT SHOWS sums the "benefitCode" + "benefitName"
  Quantity = 'requestedQuantity',
  AmountClaimed = 'amountClaimed',
  AmountBenefit = 'amountBenefit',
  TotalAmount = 'totalAmount', // THIS IS DUMMY COLUMN THAT SHOWS sums the "APPROVED AMT" and "BENEFIT AMT" values
  AdjudicationStatus = 'adjudicationStatus',
}

@Component({
  selector: 'app-utilisations-overview',
  templateUrl: './utilisation-overview.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss',
    '../../../../../../styles/global/_dialog-styles.scss',
    './utilisation-overview.component.scss',
  ],
  animations: [detailsExpand],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class UtilisationOverviewComponent
  implements OnInit, OnChanges, OnDestroy
{
  readonly destroyed$ = new Subject();
  readonly lookupValue = LookupValue;
  readonly dateFormat = DateUtil.dateFormat;
  readonly controls = RequestBuilderFunctions.getTableControls();
  readonly tableColumns = TableColumns;
  readonly maxYear = new Date();

  @Input() policyId: number;
  @Input() policyNumber: string;

  tableControl: TableControl;
  displayedColumns: string[] = Object.values(TableColumns);
  dataSourcePha: MatTableDataSource<ActivityDisplayDTO> = new MatTableDataSource();
  dataSourceMedscheme: MatTableDataSource<ActivityMedschemeDisplayDTO> = new MatTableDataSource();
  utilisationExpandedRow: ActivityDisplayDTO | null;
  admin: String;
  dateForm: FormControl;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('utilisationPaginator', { static: false })
  utilisationPaginator: MatPaginator;

  constructor(
    private activityService: ActivityService,
    private sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public basicPersonDetails: BasicPersonDetailDTO
  ) {
    this.tableControl = new TableControl(
      this.controls.length,
      this.controls.pageSize,
      this.controls.pageNumber,
      this.controls.pageSizeOptions,
      'startDate',
      'DESCENDING',
      '',
      ''
    );
  }
  ngOnInit(): void {
    this.dateForm = new FormControl(moment(new Date()), [Validators.required]);
  }

  @ViewChild('MatSortUtilisationStep') set matSort(ms: MatSort) {
    if (!ms) {
      return;
    }
    this.dataSourcePha.sortingDataAccessor = (item: ActivityDisplayDTO, property: string) => {
      switch (property) {
        case TableColumns.Benefit:
          // Note:
          //  Column shows sums the "benefitCode" + "benefitName"
          //  So currently filter this column by benefitCode.
          return item['benefitCode'];
        case TableColumns.AdjudicationStatus:
        case TableColumns.Provider:
          return item[property].name;
        case property:
          return item[property];
        default:
          return null;
      }
    };
    this.dataSourcePha.sort = ms;
  }

  get isDataExist(): boolean {
    return (
      (this.dataSourcePha &&
        this.dataSourcePha.data &&
        this.dataSourcePha.data.length > 0)
      ||(this.dataSourceMedscheme &&
        this.dataSourceMedscheme.data &&
        this.dataSourceMedscheme.data.length > 0)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.policyId) {
      this.policyId = changes.policyId.currentValue;
      this.getListOfActivities(this.tableControl);
    }
  }

  isSearchButtonDisabled(): boolean {
    if (this.dateForm?.value) {
      return false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  pageChanged($event: PageEvent): void {
    this.tableControl.pageNumber = $event.pageIndex;
    this.tableControl.pageSize = $event.pageSize;
    this.getListOfActivities(this.tableControl);
  }

  setYear(normalizedMonthAndYear: moment.Moment, datepicker: MatDatepicker<moment.Moment>) {
    datepicker.close();
    const ctrlValue = moment();
    ctrlValue.month(normalizedMonthAndYear.month());
    ctrlValue.year(normalizedMonthAndYear.year());
    this.dateForm.setValue(ctrlValue);
  }

  getListOfActivities(tableControl: TableControl): void {
    if (!this.policyId || !this.policyNumber) {
      return;
    }

    const pagedRequestDTO =
      RequestBuilderFunctions.getTblActivityPagedRequestDTO(
        tableControl,
        String(this.policyId)
      );
    const membershipFilterDTO = new PagedFilterDTO();
    membershipFilterDTO.name = 'membershipNumber';
    membershipFilterDTO.value = this.policyNumber;
    pagedRequestDTO.fieldMap[`membershipNumber`] = membershipFilterDTO;

    const yearFilterDTO = new PagedFilterDTO();
    yearFilterDTO.name = 'year';
    yearFilterDTO.value = this.dateForm?.value?.format('YYYY') ?? null;
    pagedRequestDTO.fieldMap[`year`] = yearFilterDTO;

    this.activityService.getListOfBothAdminActivities(pagedRequestDTO,this.basicPersonDetails.optionCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: UtilisationPhaResponseDTO | UtilisationMedschemeResponseDTO) => {
          if (response.adminStatus === Admin.Medscheme) {
            this.admin = Admin.Medscheme;
            this.displayedColumns = ['benefit', 'unit', 'utilisation', 'limit'];
            this.dataSourceMedscheme.data = response.pagedQueryResultDTO
              .resultList as ActivityMedschemeDisplayDTO[];
          } else {
            this.admin = Admin.Pha;
            this.displayedColumns = Object.values(TableColumns);
            this.dataSourcePha.data = response.pagedQueryResultDTO
              .resultList as ActivityDisplayDTO[];
          }

          this.tableControl.length = response.pagedQueryResultDTO.totalRecords;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }
}
